/**
 * Reusable comment thread (list + composer) for one item: the ticket itself, a
 * checklist row, or a daily progress report. The owner stores the array and
 * persists it from `onPost`; this component never writes anywhere on its own.
 */

import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { TaskComment } from './actionTicketModel';

export type ItemCommentsThreadProps = {
  value: TaskComment[];
  /** Called with the full thread after a comment or reply is appended. */
  onPost: (next: TaskComment[]) => void | Promise<void>;
  /** When set, the composer is hidden (past version, or a read-only surface). */
  readOnly?: boolean;
  currentUserGuid: string;
  currentUserName?: string;
  /**
   * Names the item in aria-labels so several threads on one page stay
   * distinguishable, e.g. "sub-task 2".
   */
  label?: string;
  /** Wraps the thread in a toggle with a count; closed unless `defaultOpen`. */
  collapsible?: boolean;
  defaultOpen?: boolean;
  emptyText?: string;
};

function relativeTime(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  const deltaSec = Math.round((Date.now() - ms) / 1000);
  if (deltaSec < 60) return 'just now';
  const minutes = Math.round(deltaSec / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 14) return `${days}d ago`;
  try {
    return new Date(ms).toLocaleDateString();
  } catch {
    return iso;
  }
}

function initialsFromName(name: string | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

function byCreatedAtAsc(a: TaskComment, b: TaskComment): number {
  return Date.parse(a.created_at) - Date.parse(b.created_at);
}

function byCreatedAtDesc(a: TaskComment, b: TaskComment): number {
  return Date.parse(b.created_at) - Date.parse(a.created_at);
}

type Thread = {
  comment: TaskComment;
  replies: TaskComment[];
};

/** Newest top-level first; replies under each parent in conversation order. */
function buildThreads(comments: TaskComment[]): Thread[] {
  const tops: TaskComment[] = [];
  const repliesByParent = new Map<string, TaskComment[]>();
  for (const comment of comments) {
    if (!comment.parent_id) {
      tops.push(comment);
      continue;
    }
    const list = repliesByParent.get(comment.parent_id) ?? [];
    list.push(comment);
    repliesByParent.set(comment.parent_id, list);
  }
  tops.sort(byCreatedAtDesc);
  return tops.map((comment) => ({
    comment,
    replies: [...(repliesByParent.get(comment.id) ?? [])].sort(byCreatedAtAsc),
  }));
}

const CommentBody: React.FC<{
  comment: TaskComment;
  onReply?: () => void;
  indented?: boolean;
}> = ({ comment, onReply, indented }) => {
  const displayName = comment.user_name?.trim() || 'Unknown user';
  return (
    <div
      className={
        indented
          ? 'tw:ml-10 tw:border-l tw:border-border tw:pl-3'
          : 'tw:border-b tw:border-border tw:pb-3 last:tw:border-b-0 last:tw:pb-0'
      }
    >
      <div className="tw:mb-1 tw:flex tw:items-start tw:gap-2">
        <span
          className="tw:mt-0.5 tw:inline-flex tw:h-7 tw:w-7 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-muted tw:text-[10px] tw:font-semibold tw:text-muted-foreground"
          aria-hidden
        >
          {initialsFromName(displayName)}
        </span>
        <div className="tw:min-w-0 tw:flex-1">
          <div className="tw:flex tw:flex-wrap tw:items-baseline tw:gap-x-2 tw:gap-y-0.5">
            <span className="tw:text-sm tw:font-medium tw:text-foreground">
              {displayName}
            </span>
            <span className="tw:text-xs tw:text-muted-foreground">
              {relativeTime(comment.created_at)}
            </span>
          </div>
          <p className="tw:m-0 tw:mt-1 tw:whitespace-pre-wrap tw:text-sm tw:text-foreground">
            {comment.body}
          </p>
          {onReply ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="tw:mt-1 tw:h-7 tw:px-2"
              onClick={onReply}
            >
              Reply
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export const ItemCommentsThread: React.FC<ItemCommentsThreadProps> = ({
  value,
  onPost,
  readOnly = false,
  currentUserGuid,
  currentUserName,
  label,
  collapsible = false,
  defaultOpen = false,
  emptyText = 'No comments yet.',
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [draft, setDraft] = useState('');
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);
  const threads = useMemo(() => buildThreads(value), [value]);
  const suffix = label ? ` on ${label}` : '';

  const post = async () => {
    const body = draft.trim();
    if (!body || !currentUserGuid || readOnly || posting) return;
    const next: TaskComment = {
      id: crypto.randomUUID(),
      parent_id: replyToId,
      body,
      user_guid: currentUserGuid,
      ...(currentUserName?.trim() ? { user_name: currentUserName.trim() } : {}),
      created_at: new Date().toISOString(),
    };
    setPosting(true);
    try {
      await onPost([...value, next]);
      setDraft('');
      setReplyToId(null);
    } finally {
      setPosting(false);
    }
  };

  const replyTarget = replyToId
    ? value.find((comment) => comment.id === replyToId)
    : null;

  const composer = !readOnly ? (
    <div className="tw:flex tw:flex-col tw:gap-2">
      {replyTarget ? (
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:text-xs tw:text-muted-foreground">
          <span>Replying to {replyTarget.user_name?.trim() || 'comment'}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="tw:h-7"
            onClick={() => setReplyToId(null)}
          >
            Cancel
          </Button>
        </div>
      ) : null}
      <Textarea
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder={replyToId ? 'Write a reply…' : 'Add a comment…'}
        rows={3}
        aria-label={`${replyToId ? 'Reply' : 'Comment'}${suffix}`}
      />
      <div className="tw:flex tw:justify-end">
        <Button
          type="button"
          size="sm"
          disabled={!draft.trim() || !currentUserGuid || posting}
          aria-label={`${replyToId ? 'Post reply' : 'Post comment'}${suffix}`}
          onClick={() => {
            void post();
          }}
        >
          {replyToId ? 'Reply' : 'Comment'}
        </Button>
      </div>
    </div>
  ) : null;

  const body = (
    <div className="tw:flex tw:flex-col tw:gap-3">
      {composer}
      {threads.length === 0 ? (
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          {emptyText}
        </p>
      ) : (
        <div className="tw:flex tw:flex-col tw:gap-3">
          {threads.map(({ comment, replies }) => (
            <div key={comment.id} className="tw:flex tw:flex-col tw:gap-2">
              <CommentBody
                comment={comment}
                onReply={
                  readOnly
                    ? undefined
                    : () => {
                        setReplyToId(comment.id);
                      }
                }
              />
              {replies.map((reply) => (
                <CommentBody key={reply.id} comment={reply} indented />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (!collapsible) return body;

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="tw:w-fit"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {open ? <ChevronDown /> : <ChevronRight />}
        {value.length > 0 ? `Comments (${value.length})` : 'Comments'}
      </Button>
      {open ? body : null}
    </div>
  );
};

export default ItemCommentsThread;
